import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';

const fmt = (v) => v == null ? '—' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtPct = (v, d = 2) => v == null ? '—' : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d }) + '%';
const fmtPp = (v) => v == null ? '—' : (v > 0 ? '+' : '') + Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' p.p.';

const COLORS = { imovel: '#059669', positivo: '#0ea5e9', negativo: '#f43f5e', neutro: '#94a3b8' };

function DeltaBadge({ v }) {
  if (v == null) return <span className="text-stone-400 text-xs">—</span>;
  if (v > 0) return <span className="flex items-center gap-0.5 text-emerald-700 text-xs font-semibold"><TrendingUp size={11} />{fmtPct(v)}</span>;
  if (v < 0) return <span className="flex items-center gap-0.5 text-red-600 text-xs font-semibold"><TrendingDown size={11} />{fmtPct(v)}</span>;
  return <span className="flex items-center gap-0.5 text-stone-500 text-xs"><Minus size={11} />0,00%</span>;
}

export default function BenchmarkComparison({ benchmarks, loading, result, formData, onFormDataChange }) {
  if (!result) return null;

  const valorImovel = result.valor_imovel_final || 0;
  const totalInv = result.total_proposta || 0;
  const retornoImovel = totalInv > 0 ? ((valorImovel / totalInv) - 1) * 100 : null;
  const prazoMeses = (result.projecoes || []).length || formData?.prazo_meses || 0;
  const indDesc = formData?.indice_desconto || 'CDI';

  const tabelaRows = loading ? [] : (benchmarks?.benchmarks || []);
  const rowsFinanceiro = tabelaRows.filter(b => b.categoria !== 'agro');
  const rowsAgro       = tabelaRows.filter(b => b.categoria === 'agro');
  const prazoAnos = (prazoMeses / 12).toFixed(1);

  const momentoSaida = formData?.momento_saida || 'pos_chaves';
  const momentoLabels = {
    meio_obra:    '🏗 Meio de obra',
    final_obra:   '🏛 Final de obra',
    pos_chaves:   '🔑 Pós-chaves',
    pos_quitacao: '✅ Após quitação',
  };

  // Dados para o gráfico de barras: valor futuro equivalente vs imóvel
  const barData = [
    { nome: 'Imóvel TesIQ', valor: valorImovel, color: COLORS.imovel },
    ...tabelaRows
      .filter(b => b.disponivel && b.valor_futuro_equivalente != null)
      .map(b => ({
        nome: b.nome,
        valor: b.valor_futuro_equivalente,
        color: b.valor_futuro_equivalente <= valorImovel ? COLORS.positivo : COLORS.negativo,
      })),
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-[#04342c] rounded-2xl px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-white font-bold text-base tracking-wide">Análise de Retorno e Custo de Oportunidade</h3>
            <p className="text-[#9ee4c4] text-xs mt-0.5">
              Benchmarks históricos · Mesmo fluxo de aportes aplicado a cada alternativa
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] uppercase tracking-widest text-[#9ee4c4]">Prazo analisado</p>
            <p className="text-white font-mono font-semibold text-sm">{prazoMeses} meses ({prazoAnos} anos)</p>
          </div>
        </div>

        {/* Momento de Saída — seletor na tela de projeção */}
        {onFormDataChange && (
          <div className="mt-4 pt-4 border-t border-[#0a6b50]">
            <p className="text-[10px] uppercase tracking-widest text-[#9ee4c4] mb-2">Momento de saída do investimento</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(momentoLabels).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => onFormDataChange({ ...formData, momento_saida: value })}
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                    momentoSaida === value
                      ? 'bg-white text-[#04342c] border-white font-semibold'
                      : 'bg-transparent text-[#9ee4c4] border-[#0a6b50] hover:border-[#9ee4c4]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-[#9ee4c4]/70 mt-2">
              Benchmark calculado até: <strong className="text-[#9ee4c4]">{momentoLabels[momentoSaida]}</strong>
            </p>
          </div>
        )}
      </div>

      {/* Tabela comparativa */}
      <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-100 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-stone-800">Comparativo de Investimentos</h4>
          <span className="text-[10px] text-stone-400 uppercase tracking-widest">Mesmo fluxo de caixa</span>
        </div>

        {loading ? (
          <div className="p-8 flex items-center gap-3 text-stone-400">
            <div className="w-4 h-4 border-2 border-stone-300 border-t-emerald-600 rounded-full animate-spin" />
            <span className="text-sm">Buscando dados históricos (BCB, B3, ANBIMA)...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
                  <th className="text-left px-5 py-2.5 font-semibold">Alternativa</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Retorno 12M</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Valor equivalente</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Δ vs. imóvel</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Alpha</th>
                </tr>
              </thead>
              <tbody>
                {/* Linha do imóvel */}
                <tr className="border-b border-stone-100 bg-emerald-50/60">
                  <td className="px-5 py-3 font-semibold text-emerald-800">
                    Imóvel TesIQ
                    {retornoImovel != null && (
                      <span className="ml-2 text-[10px] text-emerald-600 font-normal">
                        ({fmtPct(retornoImovel)} no período)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-stone-400 text-xs">Valorização projetada</td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-700">{fmt(valorImovel)}</td>
                  <td className="px-4 py-3 text-right text-stone-400">—</td>
                  <td className="px-4 py-3 text-right text-stone-400">—</td>
                </tr>

                {rowsFinanceiro.map(b => (
                  <tr key={b.id} className={`border-b border-stone-100 hover:bg-stone-50 transition-colors ${!b.disponivel ? 'opacity-50' : ''}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-stone-800">{b.nome}</div>
                      {b.fonte && (
                        <div className="text-[10px] text-stone-400 mt-0.5 flex items-center gap-1">
                          <Info size={9} /> {b.fonte}
                          {b.atualizado_em && ` · atualizado ${b.atualizado_em}`}
                        </div>
                      )}
                      {!b.disponivel && b.info && (
                        <div className="text-[10px] text-amber-600 mt-0.5">{b.info}</div>
                      )}
                      {!b.disponivel && b.erro && (
                        <div className="text-[10px] text-red-500 mt-0.5">Dados históricos temporariamente indisponíveis.</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-stone-700">
                      {b.disponivel ? fmtPct(b.retorno_12m) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {b.disponivel && b.valor_futuro_equivalente != null ? (
                        <span className={b.valor_futuro_equivalente <= valorImovel ? 'text-sky-700' : 'text-red-600'}>
                          {fmt(b.valor_futuro_equivalente)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {b.disponivel && b.diferenca_rs != null ? (
                        <span className={`font-mono font-semibold text-xs ${b.diferenca_rs >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                          {b.diferenca_rs >= 0 ? '+' : ''}{fmt(b.diferenca_rs)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DeltaBadge v={b.alpha} />
                    </td>
                  </tr>
                ))}

                {/* Separador + Commodities CEPEA */}
                {rowsAgro.length > 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-2 bg-amber-50 border-y border-amber-100">
                      <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-widest">
                        🌾 Commodities Agro (CEPEA/ESALQ) · retorno extrapolado da variação mensal
                      </span>
                    </td>
                  </tr>
                )}
                {rowsAgro.map(b => (
                  <tr key={b.id} className={`border-b border-stone-100 hover:bg-amber-50/40 transition-colors ${!b.disponivel ? 'opacity-50' : ''}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-amber-900">{b.nome}</div>
                      {b.fonte && (
                        <div className="text-[10px] text-stone-400 mt-0.5 flex items-center gap-1">
                          <Info size={9} /> {b.fonte}
                        </div>
                      )}
                      {b.nota && (
                        <div className="text-[10px] text-amber-600 mt-0.5 italic">{b.nota}</div>
                      )}
                      {!b.disponivel && b.info && (
                        <div className="text-[10px] text-amber-600 mt-0.5">{b.info}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-amber-800">
                      {b.disponivel ? fmtPct(b.retorno_12m) + '*' : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {b.disponivel && b.valor_futuro_equivalente != null ? (
                        <span className={b.valor_futuro_equivalente <= valorImovel ? 'text-sky-700' : 'text-red-600'}>
                          {fmt(b.valor_futuro_equivalente)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {b.disponivel && b.diferenca_rs != null ? (
                        <span className={`font-mono font-semibold text-xs ${b.diferenca_rs >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                          {b.diferenca_rs >= 0 ? '+' : ''}{fmt(b.diferenca_rs)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DeltaBadge v={b.alpha} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Gráfico de barras */}
      {!loading && barData.length > 1 && (
        <div className="bg-white border border-stone-200 rounded-xl shadow-sm p-5">
          <p className="text-xs font-semibold text-stone-600 uppercase tracking-widest mb-4">
            Valor acumulado com o mesmo fluxo de aportes
          </p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={barData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="nome" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 10 }}
                tickFormatter={v => 'R$ ' + (v >= 1e6 ? (v / 1e6).toFixed(1) + 'M' : (v / 1e3).toFixed(0) + 'k')}
              />
              <Tooltip formatter={v => fmt(v)} />
              <Bar dataKey="valor" radius={[4, 4, 0, 0]}>
                {barData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="text-[10px] text-stone-400 mt-2 text-center">
            CDI/Selic/Poupança: BCB SGS. Tesouro/CDB: líquido de IR (tabela regressiva). IFIX/Multimercados: em breve.
          </p>
        </div>
      )}

      {/* Custo de Oportunidade vs referência principal */}
      {result.custo_oportunidade_valor_final != null && (
        <div className={`rounded-xl border p-5 ${result.custo_oportunidade_delta >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-start justify-between">
            <div>
              <p className={`text-xs font-bold uppercase tracking-widest mb-1 ${result.custo_oportunidade_delta >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                Custo de Oportunidade vs {indDesc}
              </p>
              <p className={`text-2xl font-mono font-bold ${result.custo_oportunidade_delta >= 0 ? 'text-emerald-800' : 'text-amber-800'}`}>
                {result.custo_oportunidade_delta >= 0 ? '+' : ''}{fmt(result.custo_oportunidade_delta)}
              </p>
              <p className={`text-xs mt-1 ${result.custo_oportunidade_delta >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                {result.custo_oportunidade_delta >= 0
                  ? `O imóvel supera o ${indDesc} em ${fmtPct(result.custo_oportunidade_percentual)} no período analisado.`
                  : `Mantendo o mesmo fluxo no ${indDesc}, o capital teria produzido ${fmt(Math.abs(result.custo_oportunidade_delta))} a mais.`}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-stone-500 uppercase tracking-widest">{indDesc} renderia</p>
              <p className="font-mono font-semibold text-stone-700 text-sm">{fmt(result.custo_oportunidade_valor_final)}</p>
              {result.fonte_taxa_desconto && (
                <p className="text-[10px] text-stone-400 mt-1">
                  {result.fonte_taxa_desconto.includes('fallback') ? 'CDI estimado' : result.fonte_taxa_desconto.replace('BCB SGS série 12 — ', '')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rodapé de transparência */}
      <div className="text-[10px] text-stone-400 space-y-0.5 px-1">
        <p><span className="font-semibold text-stone-500">DADOS INFORMADOS PELO USUÁRIO:</span> valorização projetada de {fmtPct(formData?.valorizacao_anual)} a.a.</p>
        <p><span className="font-semibold text-stone-500">DADOS HISTÓRICOS:</span> CDI, Selic e Poupança — BCB SGS. Tesouro/CDB: líquido de IR (tab. regressiva). *Commodities: retorno extrapolado da variação mensal CEPEA/ESALQ.</p>
        <p><span className="font-semibold text-stone-500">CÁLCULOS TESIQ:</span> TIR por datas reais (XIRR), VPL e benchmark com mesmo fluxo financeiro.</p>
        <p><span className="font-semibold text-stone-500">PREMISSA:</span> venda projetada em {prazoMeses} meses por {fmt(valorImovel)}.</p>
      </div>
    </div>
  );
}
